import { useEffect, useState } from 'react';
import { PricingCard } from './pricing/PricingCard';
import { Heading } from '../typography/Heading';
import { Text } from '../typography/Text';
import { FadeIn } from '../animation/FadeIn';
import { Mail } from 'lucide-react';
import {
  addDoc,
  collection,
  DocumentReference,
  getDocs,
  query,
  where,
  WhereFilterOp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../utils/firebase/config';
import { auth } from '../../utils/firebase/firebase';
import { signInAsAnonymous } from '../../utils/firebase';

async function queryDocuments<T>(
  collectionName: string,
  conditions: { field: string; operator: WhereFilterOp; value: boolean }[]
): Promise<T[]> {
  const collectionRef = collection(db, collectionName);
  const q = query(
    collectionRef,
    ...conditions.map((cond) => where(cond.field, cond.operator, cond.value))
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as T));
}

export interface Plan {
  title: string;
  description: string;
  price: string;
  duration: string;
  features: string[];
  isLimitedOffer?: boolean;
  stripeButtonId: string;
  priceId: string;
}

const isStringNumber = (str: string | undefined): number | null => {
  const num = Number(str);
  return isNaN(num) ? null : num;
};

export interface Product {
  price: ProductPrice;
  active: boolean;
  description: string;
  id: string;
  images: string[];
  metadata: Metadata;
  name: string;
  popular: boolean;
}

export interface Metadata {
  popular?: 0 | 1;
  oldPrice?: string;
  [key: string]: string | number | undefined;
}

export interface ProductPrice {
  id: string;
  tax_behavior: string;
  interval_count: number;
  billing_scheme: string;
  tiers: null;
  product: string;
  description: null;
  metadata: Metadata;
  type: string;
  trial_period_days: null;
  tiers_mode: null;
  transform_quantity: null;
  active: boolean;
  recurring: {
    interval: string;
    interval_count: 1;
    aggregate_usage: null;
    usage_type: string;
    meter: null;
    trial_period_days: null;
  };
  unit_amount: number;
  interval: string;
  currency: string;
}

export function toPlan(p: Product): Plan | null {
  console.log('Converting product to plan:', p);
  // Check if there's an active price
  if (!p.price || !p.price.active) {
    console.log('No active price found for product:', p.id);
    return null;
  }

  // duration: если recurring есть — берем interval, иначе 'one-time'
  let duration = 'one-time';
  if (p.price.recurring && p.price.recurring.interval) {
    duration = p.price.recurring.interval;
  }

  // Extract features in order
  const features = Object.entries(p.metadata)
    .filter(([key]) => key.startsWith('feat'))
    .sort(([a], [b]) => {
      const numA = parseInt(a.replace('feat', ''));
      const numB = parseInt(b.replace('feat', ''));
      return numA - numB;
    })
    .map(([_, value]) => value)
    .filter((v): v is string => typeof v === 'string' && Boolean(v));

  console.log('Extracted features:', features);

  const plan = {
    title: p.name,
    price: (p.price.unit_amount / 100).toString(),
    description: p.description,
    features,
    isLimitedOffer: Boolean(p.metadata.popular),
    stripeButtonId: p.id,
    priceId: p.price.id,
    duration: duration,
  };

  console.log('Created plan:', plan);
  return plan;
}

export async function loadPrices(callback: (v: Product[]) => void) {
  try {
    console.log('Starting to load prices...');
    const snapshot = await queryDocuments<Product>('products', [
      {
        field: 'active',
        operator: '==',
        value: true,
      },
    ]);
    console.log('Initial products snapshot:', snapshot);

    const result = await Promise.all(
      snapshot.map(async (doc) => {
        console.log('Processing product:', doc.id);
        const priceSnap = await getDocs(
          collection(db, 'products', doc.id, 'prices')
        );
        const activePrices = priceSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((it) => (it as ProductPrice).active);
        
        console.log('Active prices for product:', doc.id, activePrices);
        
        const price = activePrices[0] as ProductPrice;
        
        if (!price) {
          console.log('No active price found for product:', doc.id);
          return null;
        }

        // Проверяем структуру цены
        console.log('Price structure for product:', doc.id, {
          hasRecurring: !!price.recurring,
          hasInterval: price.recurring?.interval,
          unitAmount: price.unit_amount,
          active: price.active
        });

        return {
          ...doc,
          price,
          active: true,
        };
      })
    );
    const validProducts = result.filter(Boolean) as Product[];
    console.log('Final valid products:', validProducts);
    callback(validProducts);
  } catch (error) {
    console.error('Error in loadPrices:', error);
  }
}

export function getUserId(): string | null {
  return auth.currentUser?.uid || null;
}

export async function startCheckoutSession(
  price: string,
  mode: 'payment' | 'subscription',
  onStart: (start: unknown) => void,
  onSuccess: (success: DocumentReference) => void,
  onError: (err: unknown) => void
) {
  let userId = getUserId();
  if (!userId) {
    const user = await signInAsAnonymous();
    userId = user.uid;
  }
  addDoc(collection(db, 'customers', userId, 'checkout_sessions'), {
    price,
    mode,
    success_url: window.location.origin,
    cancel_url: window.location.origin + window.location.pathname + '?canceled=true',
    expires_at: Math.floor(Date.now() / 1000) + 60 * 30 // 30 минут
  }).then((docRef) => {
    onStart('start');
    onSuccess(docRef);
  }, onError);
}

export function Pricing() {
  const [plans, setPlans] = useState<Plan[]>([]);
  useEffect(() => {
    loadPrices((data) => {
      console.log('Raw data from loadPrices:', data);
      const validPlans = data
        .map((it) => toPlan(it))
        .filter((plan): plan is Plan => plan !== null)
        // Оставляем только нужные товары по id
        .filter((plan) => {
          console.log('Checking plan:', plan.stripeButtonId);
          const isIncluded = [
            'prod_RpjdGSyiL4w4KV',
            'prod_RQhpCluLN4Clg1',
          ].includes(plan.stripeButtonId);
          console.log('Is plan included:', isIncluded);
          return isIncluded;
        });

      console.log('Valid plans after filtering:', validPlans);

      // Sort plans to put popular plan last
      const sortedPlans = validPlans.sort((a, b) => {
        if (a.isLimitedOffer && !b.isLimitedOffer) return 1;
        if (!a.isLimitedOffer && b.isLimitedOffer) return -1;
        return 0;
      });

      console.log('Final sorted plans:', sortedPlans);
      setPlans(sortedPlans);
    });
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 min-h-screen">
      <FadeIn className="flex flex-col items-center text-center mb-16">
        <Heading level={1} align="center" className="mb-4">
          Choose Your Plan
        </Heading>
        <Text size="lg" align="center" className="text-slate-light max-w-2xl">
          Select the perfect plan for your needs. All plans include our core features.
        </Text>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto mb-12">
        {plans.map((plan) => (
          <PricingCard key={plan.priceId} {...plan} />
        ))}
      </div>

      <div className="text-center mt-16 space-y-8">
        <Text className="text-slate-light">
          14-day money-back guarantee. No questions asked.
        </Text>

        <div className="max-w-2xl mx-auto bg-gradient-to-r from-primary/5 via-blue-500/5 to-primary/5 rounded-2xl p-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Mail className="w-5 h-5 text-primary" />
            <Text className="text-slate font-medium">Need help choosing?</Text>
          </div>
          <Text className="text-slate-light">
            Have questions or want to share feedback? Email us at{' '}
            <a 
              href="mailto:info@coverlettergenerator.ai" 
              className="text-primary hover:text-primary-hover transition-colors"
            >
              info@coverlettergenerator.ai
            </a>
            {' '}— we respond to everyone!
          </Text>
        </div>
      </div>
    </div>
  );
}